import { distinctOverlayGroups, type EditableTemplate } from '../../lib/adminTemplates';

/**
 * Read-only rendering of the overlay grouping the current draft would produce — headers and column
 * labels only, no values (WP10 spec). Mirrors the OBS overlay's own grouping: an "Overview" bucket
 * for ROOM-scoped fields (the overlay always prepends this itself, per `lib/templates.ts`'s
 * `overlayGroups` doc comment) followed by each distinct `overlayGroup` in field order.
 */
export function TemplatePreview({ template }: { template: EditableTemplate }) {
  const fields = template.sections.flatMap((section) => section.fields);
  const overview = fields.filter((field) => field.overlayGroup == null);
  const groups = distinctOverlayGroups(template).map((group) => ({
    group,
    fields: fields.filter((field) => field.overlayGroup === group),
  }));

  const hasAnything = overview.length > 0 || groups.length > 0;

  return (
    <section className="mt-10">
      <div className="flex items-baseline gap-4">
        <span className="eyebrow">Preview / Overlay grouping</span>
        <span className="h-px flex-1 bg-border" aria-hidden="true" />
      </div>

      {!hasAnything && <p className="mt-4 telemetry text-text-faint">No fields yet.</p>}

      {hasAnything && (
        <div className="mt-4 flex flex-col gap-5">
          {overview.length > 0 && (
            <div>
              <h3 className="eyebrow text-text">Overview</h3>
              <ul className="mt-1 flex flex-wrap gap-2">
                {overview.map((field) => (
                  <li
                    key={field.localId}
                    className="telemetry rounded-full border border-border-strong px-2.5 py-1 text-text-muted"
                  >
                    {field.label || '(untitled)'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {groups.map(({ group, fields: groupFields }) => (
            <div key={group}>
              <h3 className="eyebrow text-text">{group}</h3>
              <ul className="mt-1 flex flex-wrap gap-2">
                {groupFields.map((field) => (
                  <li
                    key={field.localId}
                    className="telemetry rounded-full border border-border-strong px-2.5 py-1 text-text-muted"
                  >
                    {field.label || '(untitled)'}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
